<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Loader2 } from 'lucide-vue-next'
import { zmgPublicCacheClient, zmgAdminOnlyCacheUpdateClient } from '@api-client/zmg_cache'
import { onMounted, ref } from 'vue'
import { toast } from "@iframe-widgets/pages/widgets/toast/iframeToast"

const SAMPLE_TERMS = `All sales are final and non-refundable.

We are willing to help resolve any issues you may have.

Please contact us first by phone or by visiting the business location so we can work with you to find a solution.

Please note that a dispute fee (currently £20) is charged for each dispute, regardless of whether the dispute is ultimately won or lost. This creates a direct loss as a result of the dispute.

To prevent abuse of the dispute process, this policy applies to all sales: all sales are final and non-refundable.

By placing this order, you agree to contact us by phone or by visiting the business location first and allow a reasonable opportunity (minimum 10 business days) to resolve any issue before initiating a chargeback or payment dispute with your bank or credit card company.`;

const props = defineProps<{
    storeId: string
}>()

const emit = defineEmits<{
    (e: 'saved'): void
}>()

const TERMS_MAX = 1200;

const isLoading = ref(false)
const isSaving = ref(false)
const termsText = ref('')

const loadData = async () => {
    isLoading.value = true
    const { data, error } = await (await zmgPublicCacheClient.store.store_terms[':id']['data.json'].$get({
        param: {
            id: props.storeId,
        },
    })).json()
    if (error) {
        toast.error(error.message || 'Failed to load store terms')
    } else if (data) {
        termsText.value = data.termsText ?? ''
    } else {
        termsText.value = ''
    }
    isLoading.value = false
}

onMounted(() => {
    loadData()
})

async function saveTerms() {
    isSaving.value = true
    try {
        const trimmedTerms = termsText.value.trim().slice(0, TERMS_MAX)
        const { error } = await (await zmgAdminOnlyCacheUpdateClient.store.store_terms[':id'].$patch({
            param: {
                id: props.storeId,
            },
            json: {
                termsText: trimmedTerms || null,
            },
        })).json()
        if (error) {
            toast.error(error.message || 'Failed to save')
            return
        }
        termsText.value = trimmedTerms
        emit('saved')
        toast.success('Saved')
    } catch {
        toast.error('Failed to save')
    } finally {
        isSaving.value = false
    }
}

function addSampleTerms() {
    termsText.value = SAMPLE_TERMS + '\n\n' + termsText.value;
}
</script>

<template>
    <div class="space-y-6">
        <div v-if="isLoading"
             class="flex justify-center py-8">
            <Loader2 class="h-6 w-6 animate-spin" />
        </div>
        <template v-else>
            Save empty to hide terms at checkout page. Leave empty to hide.
            <div class="space-y-2">
                <Label for="terms-text">Terms (max {{ TERMS_MAX }})</Label>
                <Textarea id="terms-text"
                          v-model="termsText"
                          :maxlength="TERMS_MAX"
                          rows="10"
                          placeholder="Shown to buyers in a popover at checkout. Leave empty to hide." />
                <p class="text-muted-foreground text-xs">{{ termsText.length }}/{{ TERMS_MAX }}</p>
            </div>
            <Button class="w-full"
                    @click="saveTerms"
                    :disabled="isSaving">
                <Loader2 v-if="isSaving"
                         class="mr-2 h-4 w-4 animate-spin" />
                Save
            </Button>
            <Button variant="outline"
                    class="w-full"
                    @click="addSampleTerms">
                Add sample terms
            </Button>
        </template>
    </div>
</template>
